import type {
  Appeal,
  AppealSubmissionMethod,
  Denial,
  DenialBatch,
  DenialCategory,
  DenialStatus,
  LoginResponse,
  Outcome,
  OutcomeCreate,
  UserPublic,
} from '@nexaclaim/shared';

export interface ApiMeta {
  request_id: string;
  timestamp: string;
  page?: number;
  per_page?: number;
  total?: number;
  has_more?: boolean;
  next_cursor?: string;
}

export interface ApiEnvelope<T> {
  data: T;
  meta: ApiMeta;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

// Injected by auth_store after initialization to break circular dep.
let _getToken: () => string | null = () => null;
let _onUnauthorized: () => Promise<string | null> = async () => null;

export function configureApi(opts: {
  getToken: () => string | null;
  onUnauthorized: () => Promise<string | null>;
}) {
  _getToken = opts.getToken;
  _onUnauthorized = opts.onUnauthorized;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

function idempotencyKey(): string {
  return crypto.randomUUID();
}

async function parseEnvelope<T>(res: Response): Promise<T> {
  const body = await res.json();
  if (!res.ok) {
    const err = (body as { error?: ApiError }).error;
    throw new ApiRequestError(
      err?.code ?? 'UNKNOWN_ERROR',
      err?.message ?? 'An unexpected error occurred.',
      res.status,
      err?.details,
    );
  }
  return (body as ApiEnvelope<T>).data;
}

async function request<T>(
  method: string,
  path: string,
  opts: {
    body?: unknown;
    isUpload?: boolean;
    idempotent?: boolean;
    query?: Record<string, string | number | boolean | undefined>;
  } = {},
): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {};
  const token = _getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.idempotent) headers['X-Idempotency-Key'] = idempotencyKey();
  if (!opts.isUpload) headers['Content-Type'] = 'application/json';

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    body:
      opts.body instanceof FormData
        ? opts.body
        : opts.body
          ? JSON.stringify(opts.body)
          : undefined,
  };

  let res = await fetch(url.toString(), init);

  if (res.status === 401) {
    const newToken = await _onUnauthorized();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(url.toString(), { ...init, headers });
    }
  }

  return parseEnvelope<T>(res);
}

// ─────────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────────

export interface DashboardMetrics {
  appeals_submitted: number;
  win_rate: number;
  dollars_recovered_cents: number;
  avg_days_to_resolution: number | null;
}

export interface DashboardTrend {
  date: string;
  recovered_cents: number;
  appeals_submitted: number;
}

export interface PayerBreakdown {
  payer_id: string;
  payer_name: string;
  denial_count: number;
  total_denied_cents: number;
}

// ─────────────────────────────────────────────────────────────────────
// Denial list filters
// ─────────────────────────────────────────────────────────────────────

export interface DenialListFilters {
  status?: DenialStatus;
  payer_id?: string;
  priority?: number;
  date_from?: string;
  date_to?: string;
  assigned_to_user_id?: string;
  sort?: string;
  page?: number;
  per_page?: number;
  cursor?: string;
}

// ─────────────────────────────────────────────────────────────────────
// Denial detail with claim + payer info
// ─────────────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  action: string;
  user_id: string;
  user_name?: string;
  created_at: string;
}

export interface DenialDetail extends Denial {
  payer_name?: string;
  claim_number?: string;
  date_of_service?: string;
  original_charge_cents?: number;
  cpt_codes?: string[];
  recovery_probability_explanation?: string;
  audit_history?: AuditEntry[];
}

// ─────────────────────────────────────────────────────────────────────
// Appeal submit details
// ─────────────────────────────────────────────────────────────────────

export interface AppealSubmitDetails {
  submission_method: AppealSubmissionMethod;
  submission_tracking_number?: string;
}

// ─────────────────────────────────────────────────────────────────────
// User management
// ─────────────────────────────────────────────────────────────────────

export interface InviteUserPayload {
  email: string;
  full_name: string;
  role: string;
}

export interface UpdateUserPayload {
  role?: string;
  status?: string;
  full_name?: string;
}

export interface Organization {
  id: string;
  name: string;
  npi: string;
  subscription_tier: string;
  status: string;
}

// ─────────────────────────────────────────────────────────────────────
// API namespace object
// ─────────────────────────────────────────────────────────────────────

export const api = {
  auth: {
    login(email: string, password: string) {
      return request<LoginResponse>('POST', '/v1/auth/login', {
        body: { email, password },
      });
    },
    logout() {
      return request<void>('POST', '/v1/auth/logout');
    },
    refresh() {
      return request<{ access_token: string }>('POST', '/v1/auth/refresh');
    },
    me() {
      return request<UserPublic>('GET', '/v1/auth/me');
    },
  },

  denials: {
    list(filters: DenialListFilters = {}) {
      const {
        status,
        payer_id,
        priority,
        date_from,
        date_to,
        assigned_to_user_id,
        sort,
        page,
        per_page,
        cursor,
      } = filters;
      return request<Denial[]>('GET', '/v1/denials', {
        query: {
          'filter[status]': status,
          'filter[payer_id]': payer_id,
          'filter[priority]': priority,
          'filter[created_at][gte]': date_from,
          'filter[created_at][lte]': date_to,
          'filter[assigned_to_user_id]': assigned_to_user_id,
          sort,
          page,
          per_page,
          cursor,
        },
      });
    },
    get(id: string) {
      return request<DenialDetail>('GET', `/v1/denials/${id}`);
    },
    upload(file: File) {
      const form = new FormData();
      form.append('file', file);
      return request<DenialBatch>('POST', '/v1/denials/upload', {
        body: form,
        isUpload: true,
        idempotent: true,
      });
    },
    patch(
      id: string,
      payload: {
        status?: DenialStatus;
        assigned_to_user_id?: string;
        notes?: string;
        category?: DenialCategory;
      },
    ) {
      return request<Denial>('PATCH', `/v1/denials/${id}`, { body: payload });
    },
  },

  appeals: {
    list(denialId?: string) {
      return request<Appeal[]>('GET', '/v1/appeals', {
        query: { 'filter[denial_id]': denialId },
      });
    },
    get(id: string) {
      return request<Appeal>('GET', `/v1/appeals/${id}`);
    },
    generate(denialId: string) {
      return request<{
        id: string;
        status: string;
        estimated_completion_seconds: number;
      }>('POST', '/v1/appeals/generate', {
        body: { denial_id: denialId },
        idempotent: true,
      });
    },
    patch(id: string, payload: { letter_content?: string }) {
      return request<Appeal>('PATCH', `/v1/appeals/${id}`, { body: payload });
    },
    approve(id: string) {
      return request<Appeal>('POST', `/v1/appeals/${id}/approve`);
    },
    submit(id: string, details: AppealSubmitDetails) {
      return request<Appeal>('POST', `/v1/appeals/${id}/submit`, {
        body: details,
        idempotent: true,
      });
    },
    regenerate(id: string) {
      return request<{ id: string; status: string }>(
        'POST',
        `/v1/appeals/${id}/regenerate`,
        { idempotent: true },
      );
    },
  },

  outcomes: {
    record(appealId: string, outcome: OutcomeCreate) {
      return request<Outcome>('POST', `/v1/appeals/${appealId}/outcome`, {
        body: outcome,
      });
    },
  },

  dashboard: {
    metrics() {
      return request<DashboardMetrics>('GET', '/v1/dashboard/metrics');
    },
    trends() {
      return request<DashboardTrend[]>('GET', '/v1/dashboard/trends');
    },
    byPayer() {
      return request<PayerBreakdown[]>('GET', '/v1/dashboard/by-payer');
    },
  },

  users: {
    list() {
      return request<UserPublic[]>('GET', '/v1/users');
    },
    invite(payload: InviteUserPayload) {
      return request<UserPublic>('POST', '/v1/users', { body: payload });
    },
    update(id: string, payload: UpdateUserPayload) {
      return request<UserPublic>('PATCH', `/v1/users/${id}`, { body: payload });
    },
    deactivate(id: string) {
      return request<void>('POST', `/v1/users/${id}/deactivate`);
    },
    resetMfa(id: string) {
      return request<void>('POST', `/v1/users/${id}/reset-mfa`);
    },
  },

  organization: {
    get() {
      return request<Organization>('GET', '/v1/organization');
    },
  },

  audit: {
    phiAccess(params?: { page?: number; per_page?: number }) {
      return request<AuditEntry[]>('GET', '/v1/audit/phi-access', {
        query: params,
      });
    },
    system(params?: { page?: number; per_page?: number }) {
      return request<AuditEntry[]>('GET', '/v1/audit/system', {
        query: params,
      });
    },
  },
};
